import React, { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { graphql } from 'react-relay';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { isSmartSpaceOverviewEnabled } from './isSmartSpaceOverviewEnabled.ts';

import type {
	SpaceOverviewSwitcherQuery$data,
	SpaceOverviewSwitcherQuery$variables,
} from './__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { SpaceOverview } from './SpaceOverview.tsx';
import { SmartSpaceOverview } from './smart-space-overview/SmartSpaceOverview.tsx';

export const SpaceOverviewSwitcherQuery = graphql`
	query SpaceOverviewSwitcherQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_smartSpaceOverview(cloudId: $cloudId, spaceKey: $spaceKey) {
			summary {
				body
				isPinned
			}
			faq {
				entries {
					question
					answer
					followUpQuestions
					sources {
						content {
							id
							title
							excerpt
							embeddedProduct
							links {
								webui
							}
						}
					}
				}
			}
			topLinks {
				contentId
				lastRefreshed
				links {
					isPinned
					title
					url
				}
			}
		}
	}
`;

type SpaceOverviewExperience = 'smart' | 'regular';

type SpaceOverviewSwitcherProps = {
	spaceKey: string;
	children?: ReactNode;
	onExperienceResolved?: (experience: SpaceOverviewExperience) => void;
};

export const SpaceOverviewSwitcher = ({
	spaceKey,
	children,
	onExperienceResolved,
}: SpaceOverviewSwitcherProps): React.JSX.Element | null => {
	const { isRovoEnabled, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId(spaceKey);
	const trackedResolvedExperienceRef = useRef<SpaceOverviewExperience | null>(null);

	const smartSpaceOverviewEnabled = isSmartSpaceOverviewEnabled({
		spaceKey,
		isRovoEnabled,
		cloudId,
	});

	const { data, loading } = useQueryInterop<
		SpaceOverviewSwitcherQuery$data,
		SpaceOverviewSwitcherQuery$variables
	>(SpaceOverviewSwitcherQuery, {
		variables: {
			cloudId,
			spaceKey,
		},
		skip: !smartSpaceOverviewEnabled,
	});

	const smartSpaceOverviewData = data?.confluence_smartSpaceOverview;
	const hasSummary =
		smartSpaceOverviewData?.summary?.body !== null &&
		smartSpaceOverviewData?.summary?.body !== undefined;
	const resolvedExperience: SpaceOverviewExperience | null = smartSpaceOverviewEnabled
		? loading
			? null
			: hasSummary
				? 'smart'
				: 'regular'
		: 'regular';

	useEffect(() => {
		if (resolvedExperience) {
			onExperienceResolved?.(resolvedExperience);

			if (spaceId && trackedResolvedExperienceRef.current !== resolvedExperience) {
				trackedResolvedExperienceRef.current = resolvedExperience;
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'resolved',
						actionSubject: 'spaceOverviewExperience',
						source: 'spaceOverviewScreen',
						containerId: spaceId,
						attributes: {
							spaceOverviewExperience: resolvedExperience,
							spaceKey,
						},
					},
				}).fire();
			}
		}
	}, [createAnalyticsEvent, resolvedExperience, onExperienceResolved, spaceId, spaceKey]);

	if (smartSpaceOverviewEnabled) {
		if (loading) {
			return null;
		}

		if (hasSummary) {
			return (
				<SmartSpaceOverview
					spaceKey={spaceKey}
					smartSpaceOverviewData={smartSpaceOverviewData}
					cloudId={cloudId}
				/>
			);
		}
	}

	return <SpaceOverview spaceKey={spaceKey}>{children}</SpaceOverview>;
};
